import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import API from '../services/api';
import './ContractorPages.css';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');
const emptyBagRow = () => ({ bagSize: '', bagCount: '' });
const statusLabel = (value) => {
  if (value === 'plant_approved') return 'Plant Approved';
  if (value === 'plant_rejected') return 'Plant Rejected';
  return 'Pending Plant Approval';
};
const bagSummary = (rows, unit) => {
  const summary = Array.isArray(rows)
    ? rows
    .filter((row) => String(row?.bagSize || '').trim())
    .map((row) => `${row.bagSize}`)
    .join(', ')
    : '';
  return summary || String(unit || '').trim() || '—';
};
const shouldSyncAutoBagSize = (rows, previousAutoUnit) => {
  if (!Array.isArray(rows) || rows.length !== 1) return false;
  const row = rows[0] || {};
  const bagSize = String(row?.bagSize || '').trim();
  if (!bagSize) return true;
  return !!previousAutoUnit && bagSize === previousAutoUnit;
};

const ContractorWorkEntryForm = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [rates, setRates] = useState([]);
  const [entries, setEntries] = useState([]);
  const [lastAutoBagUnit, setLastAutoBagUnit] = useState('');
  const [form, setForm] = useState({
    workerName: '',
    workType: '',
    quantity: 1,
    bagDetails: [emptyBagRow()],
    workDate: todayISO(),
    remarks: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      const [rRes, eRes] = await Promise.all([
        API.get('/contractor/work-rates'),
        API.get('/contractor/work-entries', { params: { from: todayISO(), to: todayISO() } }),
      ]);
      const rateRows = Array.isArray(rRes.data) ? rRes.data : [];
      setRates(rateRows);
      setEntries(Array.isArray(eRes.data) ? eRes.data : []);
      if (!form.workType && rateRows.length > 0) {
        setForm((prev) => ({ ...prev, workType: rateRows[0].workType }));
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load work entry data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'quantity') {
      setForm((prev) => ({ ...prev, quantity: String(value || '').replace(/\D/g, '') }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onBagDetailChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      bagDetails: prev.bagDetails.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      )),
    }));
  };

  const addBagRow = () => {
    setForm((prev) => ({ ...prev, bagDetails: [...prev.bagDetails, emptyBagRow()] }));
  };

  const removeBagRow = (index) => {
    setForm((prev) => {
      const nextRows = prev.bagDetails.filter((_, rowIndex) => rowIndex !== index);
      return {
        ...prev,
        bagDetails: nextRows.length ? nextRows : [emptyBagRow()],
      };
    });
  };

  const selectedRate = useMemo(
    () => rates.find((r) => r.workType === form.workType) || null,
    [rates, form.workType]
  );

  useEffect(() => {
    const defaultBagSize = String(selectedRate?.unit || '').trim();
    if (!defaultBagSize || defaultBagSize.toLowerCase() === 'job') return;

    setForm((prev) => {
      if (!shouldSyncAutoBagSize(prev.bagDetails, lastAutoBagUnit)) return prev;
      return {
        ...prev,
        bagDetails: [{ bagSize: defaultBagSize, bagCount: '' }],
      };
    });
    setLastAutoBagUnit(defaultBagSize);
  }, [selectedRate, lastAutoBagUnit]);

  const effectiveQuantity = Number(form.quantity || 0);
  const estAmount = effectiveQuantity * Number(selectedRate?.rate || 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const normalizedBagDetails = (form.bagDetails || []).map((row) => ({
        bagSize: String(row.bagSize || '').trim(),
        bagCount: 0,
      }));
      const hasPartialBagRow = normalizedBagDetails.some((row) => row.bagSize === '');
      if (hasPartialBagRow) {
        throw new Error('Bag size sahi bharo, ya row hata do');
      }

      await API.post('/contractor/work-entries', {
        ...form,
        quantity: effectiveQuantity,
        bagDetails: normalizedBagDetails
          .filter((row) => row.bagSize),
      });
      setMessage('Work entry added successfully. Entry ab Plant Manager approval ke liye bhej di gayi hai.');
      setForm((prev) => ({ ...prev, workerName: '', quantity: 1, bagDetails: [emptyBagRow()], remarks: '' }));
      await load();
    } catch (e1) {
      setError(e1?.response?.data?.message || 'Failed to create work entry');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <Card className="contractor-card">
        <Card.Body>
          <h4 className="mb-3">Daily Work Entry</h4>
          {message && <Alert variant="success">{message}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}

          {rates.length === 0 ? (
            <Alert variant="warning">Admin has not set any work rates yet.</Alert>
          ) : (
            <Form onSubmit={onSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Worker Name</Form.Label>
                    <Form.Control name="workerName" value={form.workerName} onChange={onChange} required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Type</Form.Label>
                    <Form.Select name="workType" value={form.workType} onChange={onChange} required>
                      {rates.map((r) => (
                        <option key={r._id} value={r.workType}>{r.workType}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>No. Of Bags</Form.Label>
                    <Form.Control type="text" inputMode="numeric" pattern="[0-9]*" name="quantity" value={form.quantity} onChange={onChange} required />
                    <div className="form-text">
                      Direct no. of bags dalo. Isi hisaab se amount banega.
                    </div>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Date</Form.Label>
                    <Form.Control type="date" name="workDate" value={form.workDate} onChange={onChange} required />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Estimated Amount</Form.Label>
                    <Form.Control value={`₹${Number(estAmount || 0).toLocaleString('en-IN')}`} disabled />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <Form.Label className="mb-0">Bag Breakdown</Form.Label>
                      <Button type="button" variant="outline-secondary" size="sm" onClick={addBagRow}>Add Bag Size</Button>
                    </div>
                    <div className="contractor-bag-grid">
                      {form.bagDetails.map((row, index) => (
                        <div className="contractor-bag-row" key={`bag-${index}`}>
                          <Form.Control
                            placeholder="Bag size, e.g. 5kg"
                            value={row.bagSize}
                            onChange={(e) => onBagDetailChange(index, 'bagSize', e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeBagRow(index)}
                            disabled={form.bagDetails.length === 1}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="form-text">Yahan sirf bag sizes dalo, jaise 5kg, 30kg.</div>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control as="textarea" rows={2} name="remarks" value={form.remarks} onChange={onChange} />
                  </Form.Group>
                </Col>
              </Row>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Entry'}</Button>
            </Form>
          )}
        </Card.Body>
      </Card>

      <Card className="contractor-card">
        <Card.Body>
          <h5 className="mb-3">Today Entries</h5>
          {entries.length === 0 ? (
            <div className="text-muted">No entries for today.</div>
          ) : (
            <>
              <div className="contractor-table-wrap">
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Worker</th>
                      <th>Work Type</th>
                      <th>No. Of Bags</th>
                      <th>Bag Details</th>
                      <th>Rate</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((item) => (
                      <tr key={item._id}>
                        <td>{fmtDate(item.workDate)}</td>
                        <td>{item.workerName}</td>
                        <td>{item.workType}</td>
                      <td>{item.quantity}</td>
                      <td>{bagSummary(item.bagDetails, item.unit)}</td>
                        <td>₹{Number(item.unitRate || 0).toLocaleString('en-IN')}</td>
                        <td>₹{Number(item.totalAmount || 0).toLocaleString('en-IN')}</td>
                        <td>{statusLabel(item.workflowStatus)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="contractor-mobile-list">
                {entries.map((item) => (
                  <div className="contractor-mobile-item" key={item._id}>
                  <div className="title">{item.workerName}</div>
                  <div className="meta">{item.workType} | {item.quantity} bags</div>
                  <div className="meta">Bags: {bagSummary(item.bagDetails, item.unit)}</div>
                  <div className="meta">₹{Number(item.totalAmount || 0).toLocaleString('en-IN')}</div>
                  <div className="meta">{statusLabel(item.workflowStatus)}</div>
                </div>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default ContractorWorkEntryForm;
